import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Zap, Cable, Settings, Edit3, Server, Search, ExternalLink, Unlink, MapPin } from 'lucide-react';
import { Rack, Equipment, RackEquipment } from '../../types';
import { useProjectStore } from '../../store/projectStore';
import { useSitesStore } from '../../store/sitesStore';
import { equipmentDatabase } from '../../data/equipment';
import { getRequestTheme } from '../Stock/requests/theme';

interface RackBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  editingRack?: Rack | null;
}

const newInstanceId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `rack-eq-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** Ensure every placed item has a stable instanceId so React keys survive re-sorts. */
const withInstanceIds = (items: RackEquipment[]): RackEquipment[] =>
  items.map(eq => (eq.instanceId ? eq : { ...eq, instanceId: newInstanceId() }));

const PRESET_RACK_SIZES = [4, 6, 8, 12, 24, 42] as const;
const MIN_CUSTOM_U = 1;
const MAX_CUSTOM_U = 58;

const RackBuilder: React.FC<RackBuilderProps> = ({ isOpen, onClose, editingRack }) => {
  const [selectedRackSize, setSelectedRackSize] = useState<number>(editingRack?.size || 6);
  const [rackEquipment, setRackEquipment] = useState(() => withInstanceIds(editingRack?.equipment || []));
  const [showEquipmentList, setShowEquipmentList] = useState(false);
  const [draggedEquipment, setDraggedEquipment] = useState<{ instanceId: string; startUnit: number } | null>(null);
  const [dragHoverUnit, setDragHoverUnit] = useState<number | null>(null);
  const [targetUnit, setTargetUnit] = useState<number | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(editingRack?.name || `${selectedRackSize}U Rack`);
  const [visible, setVisible] = useState(false);
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [linkedSiteId, setLinkedSiteId] = useState<string | null>(editingRack?.siteId ?? null);
  const [showChangeSite, setShowChangeSite] = useState(false);
  const [confirmUnlinkOpen, setConfirmUnlinkOpen] = useState(false);
  const [customSizeEditing, setCustomSizeEditing] = useState(false);
  const [customSizeDraft, setCustomSizeDraft] = useState('');
  const customSizeRef = useRef<HTMLDivElement | null>(null);
  const customSizeInputRef = useRef<HTMLInputElement | null>(null);

  const {
    appTheme,
    updateRack,
    setSelectedRackEquipment,
    setEquipmentConfigOpen,
    setRackPlacementMode,
    pendingRackPlacement,
    toggleRackExistingStatus,
  } = useProjectStore();
  const { sites } = useSitesStore();

  const isDark = appTheme === 'dark';
  const theme = getRequestTheme(isDark);
  const linkedSite = linkedSiteId ? sites.find(s => s.id === linkedSiteId) : undefined;

  useEffect(() => {
    if (!isOpen) {
      setVisible(false);
      return;
    }
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isEditingName) return;
      if (confirmUnlinkOpen) {
        setConfirmUnlinkOpen(false);
        return;
      }
      if (customSizeEditing) {
        setCustomSizeEditing(false);
        setCustomSizeDraft('');
        return;
      }
      if (showChangeSite) {
        setShowChangeSite(false);
        return;
      }
      close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditingName, confirmUnlinkOpen, showChangeSite, customSizeEditing]);

  // Update editedName when editingRack or selectedRackSize changes
  useEffect(() => {
    setEditedName(editingRack?.name || `${selectedRackSize}U Rack`);
  }, [editingRack?.name, selectedRackSize]);

  // Populate form with pending rack placement data when opened from placement mode
  useEffect(() => {
    if (isOpen && pendingRackPlacement && !editingRack) {
      setSelectedRackSize(pendingRackPlacement.size);
      setRackEquipment(withInstanceIds(pendingRackPlacement.equipment));
      setEditedName(pendingRackPlacement.name);
      setLinkedSiteId(pendingRackPlacement.siteId ?? null);
    }
  }, [isOpen, pendingRackPlacement, editingRack]);

  // Reset form data when opening for a new rack (no editingRack)
  useEffect(() => {
    if (isOpen && !editingRack && !pendingRackPlacement) {
      setSelectedRackSize(6);
      setRackEquipment([]);
      setEditedName('6U Rack');
      setShowEquipmentList(false);
      setDraggedEquipment(null);
      setDragHoverUnit(null);
      setTargetUnit(null);
      setIsEditingName(false);
      setEquipmentSearch('');
      setLinkedSiteId(null);
      setShowChangeSite(false);
      setConfirmUnlinkOpen(false);
      setCustomSizeEditing(false);
      setCustomSizeDraft('');
    }
  }, [isOpen, editingRack, pendingRackPlacement]);

  // Populate form data when editing an existing rack
  useEffect(() => {
    if (isOpen && editingRack && !pendingRackPlacement) {
      setSelectedRackSize(editingRack.size);
      setRackEquipment(withInstanceIds(editingRack.equipment));
      setEditedName(editingRack.name);
      setShowEquipmentList(false);
      setDraggedEquipment(null);
      setDragHoverUnit(null);
      setTargetUnit(null);
      setIsEditingName(false);
      setEquipmentSearch('');
      setLinkedSiteId(editingRack.siteId ?? null);
      setShowChangeSite(false);
      setConfirmUnlinkOpen(false);
      setCustomSizeEditing(false);
      setCustomSizeDraft('');
    }
  }, [isOpen, editingRack, pendingRackPlacement]);

  useEffect(() => {
    if (!customSizeEditing) return;
    const id = requestAnimationFrame(() => customSizeInputRef.current?.focus());
    const onPointerDown = (e: MouseEvent) => {
      if (customSizeRef.current && !customSizeRef.current.contains(e.target as Node)) {
        setCustomSizeEditing(false);
        setCustomSizeDraft('');
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [customSizeEditing]);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 180);
  };

  if (!isOpen) return null;

  const isPresetSize = (PRESET_RACK_SIZES as readonly number[]).includes(selectedRackSize);
  const isCustomSize = !isPresetSize;

  const beginCustomSizeEdit = () => {
    setCustomSizeDraft(isCustomSize ? String(selectedRackSize) : '');
    setCustomSizeEditing(true);
  };

  const applyCustomSize = () => {
    const parsed = parseInt(customSizeDraft.trim(), 10);
    if (!Number.isFinite(parsed)) {
      setCustomSizeEditing(false);
      setCustomSizeDraft('');
      return;
    }
    const used = rackEquipment.reduce((total, eq) => total + (eq.equipment.units || 1), 0);
    const next = Math.max(MIN_CUSTOM_U, Math.min(MAX_CUSTOM_U, parsed));
    if (next < used) {
      // Don't shrink below installed equipment
      return;
    }
    setSelectedRackSize(next);
    setCustomSizeEditing(false);
    setCustomSizeDraft('');
  };

  // Calculate stats
  const usedUnits = rackEquipment.reduce((total, eq) => total + (eq.equipment.units || 1), 0);
  const openUnits = selectedRackSize - usedUnits;
  const totalPowerDraw = rackEquipment.reduce((total, eq) => {
    const power = eq.equipment.specifications?.power;
    if (power && typeof power === 'string') {
      const watts = parseInt(power.replace(/\D/g, ''));
      return total + (watts || 0);
    }
    return total;
  }, 0);
  const totalCables = rackEquipment.reduce((total, eq) => {
    return total + (eq.equipment.specifications?.ports || 0);
  }, 0);
  const fillPct = selectedRackSize > 0 ? Math.round((usedUnits / selectedRackSize) * 100) : 0;

  const addEquipmentToRack = (equipment: Equipment) => {
    let startUnit: number;

    if (targetUnit !== null) {
      startUnit = targetUnit;

      const equipmentUnits = equipment.units || 1;
      const hasOverlap = rackEquipment.some(eq => {
        const eqUnits = eq.equipment.units || 1;
        const eqEnd = eq.startUnit + eqUnits;
        const targetEnd = startUnit + equipmentUnits;
        return !(targetEnd <= eq.startUnit || startUnit >= eqEnd);
      });

      const wouldFit = startUnit + equipmentUnits <= selectedRackSize;

      if (!wouldFit || hasOverlap) {
        setShowEquipmentList(false);
        setTargetUnit(null);
        return;
      }
    } else {
      startUnit = rackEquipment.length > 0
        ? Math.max(...rackEquipment.map(eq => eq.startUnit + (eq.equipment.units || 1)))
        : 0;

      if (startUnit + (equipment.units || 1) > selectedRackSize) {
        setShowEquipmentList(false);
        setTargetUnit(null);
        return;
      }
    }

    const newEquipment = [...rackEquipment, {
      equipment,
      startUnit,
      instanceId: newInstanceId(),
      config: {}
    }];

    newEquipment.sort((a, b) => a.startUnit - b.startUnit);
    setRackEquipment(newEquipment);
    setShowEquipmentList(false);
    setTargetUnit(null);
  };

  const removeEquipment = (instanceId: string) => {
    setRackEquipment(rackEquipment.filter(eq => eq.instanceId !== instanceId));
  };

  const handleEquipmentClick = (equipment: RackEquipment, index: number) => {
    setSelectedRackEquipment('rack-builder', { equipment: equipment.equipment, index, config: equipment.config });
    setEquipmentConfigOpen(true);
  };

  const handleDragStart = (instanceId: string, startUnit: number) => {
    setDraggedEquipment({ instanceId, startUnit });
    setDragHoverUnit(startUnit);
  };

  const calculateDropZone = (mouseY: number, rackRect: DOMRect) => {
    const relativeY = mouseY - rackRect.top;
    const unitHeight = 30;
    return Math.max(0, Math.min(selectedRackSize - 1, Math.floor(relativeY / unitHeight)));
  };

  const isValidDropAt = (dropTargetUnit: number, draggedInstanceId: string, equipmentUnits: number) => {
    if (dropTargetUnit + equipmentUnits > selectedRackSize) return false;
    return !rackEquipment.some(eq => {
      if (eq.instanceId === draggedInstanceId) return false;
      const eqUnits = eq.equipment.units || 1;
      const eqEnd = eq.startUnit + eqUnits;
      const targetEnd = dropTargetUnit + equipmentUnits;
      return !(targetEnd <= eq.startUnit || dropTargetUnit >= eqEnd);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedEquipment) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const unit = calculateDropZone(e.clientY, rect);
    setDragHoverUnit(prev => (prev === unit ? prev : unit));
  };

  const clearDragState = () => {
    setDraggedEquipment(null);
    setDragHoverUnit(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedEquipment) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const dropTargetUnit = calculateDropZone(e.clientY, rect);
    const equipment = rackEquipment.find(eq => eq.instanceId === draggedEquipment.instanceId);
    if (!equipment) {
      clearDragState();
      return;
    }
    const equipmentUnits = equipment.equipment.units || 1;

    if (isValidDropAt(dropTargetUnit, draggedEquipment.instanceId, equipmentUnits)) {
      const updatedEquipment = rackEquipment.map(eq =>
        eq.instanceId === draggedEquipment.instanceId
          ? { ...eq, startUnit: dropTargetUnit }
          : eq
      );

      updatedEquipment.sort((a, b) => a.startUnit - b.startUnit);
      setRackEquipment(updatedEquipment);
    }

    clearDragState();
  };

  const handleNameEdit = () => {
    setIsEditingName(true);
  };

  const handleNameSave = () => {
    if (editedName.trim() !== '') {
      setIsEditingName(false);
    }
  };

  const handleNameCancel = () => {
    setEditedName(editingRack?.name || `${selectedRackSize}U Rack`);
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      handleNameCancel();
    }
  };

  const handleFinish = () => {
    if (editingRack) {
      updateRack(editingRack.id, {
        name: editedName.trim() || `${selectedRackSize}U Rack`,
        size: selectedRackSize,
        equipment: rackEquipment,
        siteId: linkedSiteId,
      });
      close();
    } else {
      setRackPlacementMode(true, {
        size: selectedRackSize,
        equipment: rackEquipment,
        name: editedName.trim() || `${selectedRackSize}U Rack`,
        siteId: linkedSiteId,
      });
      close();
    }
  };

  const openLinkedSite = () => {
    if (!linkedSiteId) return;
    // Persist link if editing so the site list reflects it immediately
    if (editingRack) {
      updateRack(editingRack.id, {
        name: editedName.trim() || `${selectedRackSize}U Rack`,
        size: selectedRackSize,
        equipment: rackEquipment,
        siteId: linkedSiteId,
      });
    }
    close();
    window.dispatchEvent(new CustomEvent('openSiteDetails', { detail: { siteId: linkedSiteId } }));
  };

  const confirmUnlinkSite = () => {
    setLinkedSiteId(null);
    setShowChangeSite(false);
    setConfirmUnlinkOpen(false);
  };

  const handleCancel = () => {
    setRackPlacementMode(false);
    close();
  };

  const rackFrameBg = isDark ? '#171a1f' : '#e5e7eb';
  const rackFrameBorder = isDark ? '#434a55' : '#9ca3af';
  const rackUnitLine = isDark ? '#2b3140' : '#d1d5db';
  const rackUnitNum = isDark ? '#6b7280' : '#9ca3af';
  const emptyHoverBg = isDark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.15)';
  const emptyHoverBorder = isDark ? '#22c55e' : '#4ade80';
  const dropValidBg = isDark ? 'rgba(34,197,94,0.18)' : 'rgba(187,247,208,0.9)';
  const dropInvalidBg = isDark ? 'rgba(239,68,68,0.18)' : 'rgba(254,202,202,0.9)';

  const searchQuery = equipmentSearch.trim().toLowerCase();
  const filteredEquipment = equipmentDatabase.filter(eq => {
    if (!eq.units || eq.units > openUnits) return false;

    if (searchQuery) {
      const haystack = `${eq.name} ${eq.manufacturer || ''}`.toLowerCase();
      if (!haystack.includes(searchQuery)) return false;
    }

    if (targetUnit !== null) {
      const equipmentUnits = eq.units;
      const hasOverlap = rackEquipment.some(existing => {
        const existingUnits = existing.equipment.units || 1;
        const existingEnd = existing.startUnit + existingUnits;
        const targetEnd = targetUnit + equipmentUnits;
        return !(targetEnd <= existing.startUnit || targetUnit >= existingEnd);
      });
      const wouldFit = targetUnit + equipmentUnits <= selectedRackSize;
      return wouldFit && !hasOverlap;
    }

    return true;
  });

  return (
    <>
      <div
        className="fixed inset-0 z-40 transition-opacity duration-150"
        style={{
          backgroundColor: 'rgba(0,0,0,0.5)',
          opacity: visible ? 1 : 0,
          // Let canvas clicks through while the exit animation runs (e.g. after Place on canvas)
          pointerEvents: visible ? 'auto' : 'none',
        }}
        onClick={handleCancel}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4">
        <div
          className="flex flex-col overflow-hidden rounded-lg border shadow-2xl transition-all duration-150"
          style={{
            width: '90vw',
            maxWidth: '1100px',
            height: '85vh',
            backgroundColor: theme.cardBg,
            borderColor: theme.border,
            opacity: visible ? 1 : 0,
            transform: visible ? 'scale(1)' : 'scale(0.98)',
            pointerEvents: visible ? 'auto' : 'none',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-3 flex-shrink-0"
            style={{ borderBottom: `1px solid ${theme.border}`, backgroundColor: theme.cardBg }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="flex items-center justify-center w-8 h-8 rounded flex-shrink-0"
                style={{ backgroundColor: theme.inputBg, color: theme.muted }}
              >
                <Server size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide" style={{ color: theme.muted }}>
                  Designer · Rack
                </p>
                <div className="flex items-center gap-2 mt-0.5 min-w-0">
                  {isEditingName ? (
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onBlur={handleNameSave}
                      onKeyDown={handleNameKeyDown}
                      className="text-sm font-semibold px-2 py-0.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[180px] max-w-[280px]"
                      style={{
                        backgroundColor: theme.inputBg,
                        borderColor: theme.border,
                        color: theme.text,
                      }}
                      autoFocus
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={handleNameEdit}
                      className="group flex items-center gap-1.5 text-sm font-semibold truncate rounded px-1 -ml-1 transition-colors"
                      style={{ color: theme.text }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.rowHover)}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                      title="Rename rack"
                    >
                      <span className="truncate max-w-[240px]">{editedName}</span>
                      <Edit3 size={12} className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" style={{ color: theme.muted }} />
                    </button>
                  )}
                  <span
                    className="inline-flex h-5 items-center px-2 rounded text-[11px] font-medium flex-shrink-0"
                    style={{
                      backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe',
                      color: isDark ? '#93c5fd' : '#1d4ed8',
                    }}
                  >
                    {selectedRackSize}U
                  </span>
                  {editingRack && (
                    <button
                      type="button"
                      onClick={() => toggleRackExistingStatus(editingRack.id)}
                      className="inline-flex h-5 items-center px-2 rounded text-[11px] font-medium flex-shrink-0 transition-colors"
                      style={
                        editingRack.isExisting
                          ? {
                              backgroundColor: isDark ? 'rgba(34,197,94,0.2)' : '#dcfce7',
                              color: isDark ? '#86efac' : '#15803d',
                            }
                          : {
                              backgroundColor: isDark ? 'rgba(156,163,175,0.2)' : '#f3f4f6',
                              color: isDark ? '#d1d5db' : '#4b5563',
                            }
                      }
                      title={editingRack.isExisting ? 'Mark as new infrastructure' : 'Mark as existing infrastructure'}
                    >
                      {editingRack.isExisting ? 'Existing' : 'New'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="p-1.5 rounded transition-colors flex-shrink-0"
              style={{ color: theme.muted }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.rowHover)}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
              title="Close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 flex overflow-hidden min-h-0">
            {/* Left — size + stats */}
            <div
              className="w-56 flex-shrink-0 flex flex-col overflow-y-auto"
              style={{ borderRight: `1px solid ${theme.border}`, backgroundColor: theme.cardBg }}
            >
              <div className="px-4 py-3" style={{ borderBottom: `1px solid ${theme.border}` }}>
                <p className="text-[11px] uppercase tracking-wide font-medium mb-2" style={{ color: theme.muted }}>
                  Rack Size
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {PRESET_RACK_SIZES.map(size => {
                    const active = selectedRackSize === size && !customSizeEditing;
                    return (
                      <button
                        key={size}
                        type="button"
                        onClick={() => {
                          setSelectedRackSize(size);
                          setCustomSizeEditing(false);
                          setCustomSizeDraft('');
                        }}
                        className="h-7 rounded text-xs font-medium transition-colors"
                        style={
                          active
                            ? { backgroundColor: '#2563eb', color: '#ffffff' }
                            : {
                                backgroundColor: theme.inputBg,
                                color: theme.text,
                                border: `1px solid ${theme.border}`,
                              }
                        }
                        onMouseEnter={e => {
                          if (!active) e.currentTarget.style.backgroundColor = theme.rowHover;
                        }}
                        onMouseLeave={e => {
                          if (!active) e.currentTarget.style.backgroundColor = theme.inputBg;
                        }}
                      >
                        {size}U
                      </button>
                    );
                  })}

                  <div ref={customSizeRef} className="col-span-3">
                    {customSizeEditing ? (
                      <div
                        className="flex items-center h-7 rounded border overflow-hidden"
                        style={{ borderColor: '#2563eb', backgroundColor: theme.inputBg }}
                      >
                        <input
                          ref={customSizeInputRef}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={customSizeDraft}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '');
                            setCustomSizeDraft(digits);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              applyCustomSize();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setCustomSizeEditing(false);
                              setCustomSizeDraft('');
                            }
                          }}
                          placeholder="0"
                          className="flex-1 h-full min-w-0 px-2 text-xs bg-transparent focus:outline-none"
                          style={{ color: theme.text }}
                        />
                        <span
                          className="px-2 text-xs font-medium flex-shrink-0"
                          style={{ color: theme.muted }}
                        >
                          U
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={beginCustomSizeEdit}
                        className="w-full h-7 rounded text-xs font-medium transition-colors"
                        style={
                          isCustomSize
                            ? { backgroundColor: '#2563eb', color: '#ffffff' }
                            : {
                                backgroundColor: theme.inputBg,
                                color: theme.text,
                                border: `1px solid ${theme.border}`,
                              }
                        }
                        onMouseEnter={e => {
                          if (!isCustomSize) e.currentTarget.style.backgroundColor = theme.rowHover;
                        }}
                        onMouseLeave={e => {
                          if (!isCustomSize) e.currentTarget.style.backgroundColor = theme.inputBg;
                        }}
                        title="Enter a custom rack height"
                      >
                        {isCustomSize ? `${selectedRackSize}U · Custom` : 'Custom'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 flex-1">
                <p className="text-[11px] uppercase tracking-wide font-medium mb-2" style={{ color: theme.muted }}>
                  Capacity
                </p>

                {/* Fill bar */}
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span style={{ color: theme.muted }}>{usedUnits} / {selectedRackSize}U used</span>
                    <span style={{ color: theme.text }} className="font-medium">{fillPct}%</span>
                  </div>
                  <div
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: theme.inputBg }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-200"
                      style={{
                        width: `${Math.min(fillPct, 100)}%`,
                        backgroundColor: fillPct >= 90 ? '#ef4444' : fillPct >= 70 ? '#f59e0b' : '#2563eb',
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-0">
                  {[
                    { icon: Cable, label: 'Ports', value: String(totalCables) },
                    { icon: Zap, label: 'Est. power', value: `${totalPowerDraw}W` },
                    { label: 'Used units', value: String(usedUnits) },
                    { label: 'Open units', value: String(openUnits), accent: openUnits > 0 },
                  ].map(row => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between py-2"
                      style={{ borderBottom: `1px solid ${theme.border}` }}
                    >
                      <span className="flex items-center gap-1.5 text-xs" style={{ color: theme.muted }}>
                        {row.icon ? <row.icon size={12} /> : null}
                        {row.label}
                      </span>
                      <span
                        className="text-xs font-medium"
                        style={{ color: row.accent ? (isDark ? '#86efac' : '#16a34a') : theme.text }}
                      >
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-4">
                  <p className="text-[11px] uppercase tracking-wide font-medium mb-2" style={{ color: theme.muted }}>
                    Site
                  </p>

                  {linkedSite && !showChangeSite ? (
                    <div
                      className="rounded border p-2.5 space-y-2"
                      style={{ borderColor: theme.border, backgroundColor: theme.inputBg }}
                    >
                      <div className="flex items-start gap-2 min-w-0">
                        <div
                          className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center mt-0.5"
                          style={{ backgroundColor: theme.cardBg, color: theme.muted }}
                        >
                          <MapPin size={12} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={openLinkedSite}
                            className="group inline-flex items-center gap-1 max-w-full text-left"
                            title={`Open ${linkedSite.name}`}
                          >
                            <span
                              className="text-xs font-medium truncate group-hover:underline"
                              style={{ color: '#60a5fa' }}
                            >
                              {linkedSite.name}
                            </span>
                            <ExternalLink size={11} className="flex-shrink-0 opacity-70 group-hover:opacity-100" style={{ color: '#60a5fa' }} />
                          </button>
                          {linkedSite.location && (
                            <p className="text-[11px] truncate mt-0.5" style={{ color: theme.muted }}>
                              {linkedSite.location}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        <button
                          type="button"
                          onClick={() => setShowChangeSite(true)}
                          className="h-7 px-2 rounded text-xs font-medium transition-colors border"
                          style={{
                            borderColor: theme.border,
                            color: theme.muted,
                            backgroundColor: theme.cardBg,
                          }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.rowHover)}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = theme.cardBg)}
                          title="Change site"
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmUnlinkOpen(true)}
                          className="inline-flex items-center justify-center gap-1 h-7 px-2 rounded text-xs font-medium transition-colors border"
                          style={{
                            borderColor: isDark ? 'rgba(239,68,68,0.35)' : '#fecaca',
                            color: isDark ? '#fca5a5' : '#b91c1c',
                            backgroundColor: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
                          }}
                          title="Unlink site"
                        >
                          <Unlink size={11} />
                          Unlink
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <select
                        value={linkedSiteId || ''}
                        onChange={(e) => {
                          setLinkedSiteId(e.target.value || null);
                          setShowChangeSite(false);
                        }}
                        className="w-full h-7 px-2 text-xs rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                        style={{
                          backgroundColor: theme.inputBg,
                          borderColor: theme.border,
                          color: theme.text,
                        }}
                      >
                        <option value="">Select a site…</option>
                        {sites.map(site => (
                          <option key={site.id} value={site.id}>
                            {site.name}
                          </option>
                        ))}
                      </select>
                      {showChangeSite && linkedSiteId ? (
                        <button
                          type="button"
                          onClick={() => setShowChangeSite(false)}
                          className="w-full h-7 rounded text-xs font-medium transition-colors border"
                          style={{
                            borderColor: theme.border,
                            color: theme.muted,
                            backgroundColor: 'transparent',
                          }}
                        >
                          Cancel
                        </button>
                      ) : (
                        <p className="text-[11px]" style={{ color: theme.muted }}>
                          Link a site to show this rack on its equipment list.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Center — rack visualization */}
            <div
              className="flex-1 flex flex-col min-w-0 min-h-0"
              style={{ backgroundColor: theme.subTableBg }}
            >
              <div className="flex-shrink-0 w-full max-w-[340px] mx-auto px-4 pt-4 pb-2">
                <p className="text-[11px] uppercase tracking-wide font-medium" style={{ color: theme.muted }}>
                  Layout
                </p>
              </div>

              {/* Dedicated scroll viewport so tall racks (24U / 42U) stay fully reachable */}
              <div className="flex-1 overflow-y-auto min-h-0 px-4 pb-4 modal-scroll">
                <div
                  className="relative mx-auto"
                  style={{
                    width: 300,
                    height: selectedRackSize * 30 + 6,
                    borderRadius: 6,
                    backgroundColor: rackFrameBg,
                    border: `1px solid ${rackFrameBorder}`,
                    boxShadow: isDark ? 'inset 0 0 0 1px rgba(0,0,0,0.25)' : 'inset 0 1px 2px rgba(0,0,0,0.06)',
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={(e) => {
                    // Only clear hover when leaving the rack frame (not entering a child)
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setDragHoverUnit(null);
                    }
                  }}
                  onDrop={handleDrop}
                >
                {/* Unit Lines */}
                {Array.from({ length: selectedRackSize - 1 }, (_, i) => (
                  <div
                    key={i}
                    className="absolute w-full"
                    style={{
                      top: (i + 1) * 30 + 1,
                      borderTop: `1px solid ${rackUnitLine}`,
                    }}
                  />
                ))}

                {/* Unit Numbers */}
                {Array.from({ length: selectedRackSize }, (_, i) => (
                  <div
                    key={i}
                    className="absolute text-[10px] font-mono"
                    style={{
                      top: i * 30 + 3,
                      left: 4,
                      height: 26,
                      display: 'flex',
                      alignItems: 'center',
                      color: rackUnitNum,
                    }}
                  >
                    {i + 1}
                  </div>
                ))}

                {/* Hover drop target + ghost preview (only the U under the cursor) */}
                {draggedEquipment && dragHoverUnit !== null && (() => {
                  const equipment = rackEquipment.find(eq => eq.instanceId === draggedEquipment.instanceId);
                  if (!equipment) return null;
                  const equipmentUnits = equipment.equipment.units || 1;
                  const isValid = isValidDropAt(dragHoverUnit, draggedEquipment.instanceId, equipmentUnits);
                  const sameSlot = dragHoverUnit === draggedEquipment.startUnit;

                  return (
                    <>
                      {/* Highlight the destination U span */}
                      <div
                        className="absolute left-8 right-2 rounded border pointer-events-none transition-[top,height,background-color,border-color] duration-75"
                        style={{
                          top: dragHoverUnit * 30 + 3,
                          height: equipmentUnits * 30 - 4,
                          zIndex: 2,
                          borderColor: isValid ? '#22c55e' : '#ef4444',
                          backgroundColor: isValid ? dropValidBg : dropInvalidBg,
                          boxShadow: isValid
                            ? `inset 0 0 0 1px ${isDark ? 'rgba(34,197,94,0.45)' : 'rgba(34,197,94,0.35)'}`
                            : `inset 0 0 0 1px ${isDark ? 'rgba(239,68,68,0.45)' : 'rgba(239,68,68,0.35)'}`,
                        }}
                      />
                      {/* Ghost of the equipment at the hover position */}
                      {!sameSlot && (
                        <div
                          className="absolute left-8 right-2 text-white text-xs px-2 rounded border pointer-events-none"
                          style={{
                            top: dragHoverUnit * 30 + 3,
                            height: equipmentUnits * 30 - 4,
                            zIndex: 3,
                            backgroundColor: isValid ? 'rgba(37,99,235,0.45)' : 'rgba(239,68,68,0.35)',
                            borderColor: isValid ? 'rgba(147,197,253,0.8)' : 'rgba(252,165,165,0.8)',
                            borderStyle: 'dashed',
                            opacity: 0.9,
                          }}
                        >
                          <div className="flex items-center h-full">
                            <span className="truncate font-medium">{equipment.equipment.name}</span>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Equipment in Rack */}
                {rackEquipment.map((rackEq, index) => {
                  const instanceId = rackEq.instanceId!;
                  const isDragging = draggedEquipment?.instanceId === instanceId;
                  return (
                    <div
                      key={instanceId}
                      draggable
                      onClick={() => handleEquipmentClick(rackEq, index)}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        // Hide the native drag image so the in-rack ghost is the cue
                        try {
                          const img = document.createElement('div');
                          img.style.position = 'absolute';
                          img.style.top = '-9999px';
                          document.body.appendChild(img);
                          e.dataTransfer.setDragImage(img, 0, 0);
                          requestAnimationFrame(() => document.body.removeChild(img));
                        } catch {
                          // ignore — browser may not support custom drag image
                        }
                        handleDragStart(instanceId, rackEq.startUnit);
                      }}
                      onDragEnd={clearDragState}
                      className="absolute left-8 right-2 text-white text-xs px-2 rounded border cursor-pointer transition-colors"
                      style={{
                        top: rackEq.startUnit * 30 + 3,
                        height: (rackEq.equipment.units || 1) * 30 - 4,
                        zIndex: 1,
                        backgroundColor: isDragging ? '#1e3a8a' : '#2563eb',
                        borderColor: isDragging ? '#3b82f6' : '#1d4ed8',
                        opacity: isDragging ? 0.35 : 1,
                        borderStyle: isDragging ? 'dashed' : 'solid',
                      }}
                      onMouseEnter={e => {
                        if (!isDragging) e.currentTarget.style.backgroundColor = '#1d4ed8';
                      }}
                      onMouseLeave={e => {
                        if (!isDragging) e.currentTarget.style.backgroundColor = '#2563eb';
                      }}
                    >
                      <div className="flex items-center justify-between h-full gap-1">
                        <span className="truncate font-medium flex-1">{rackEq.equipment.name}</span>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEquipmentClick(rackEq, index);
                            }}
                            className="p-0.5 rounded hover:bg-blue-900/50"
                            title="Configure"
                          >
                            <Settings size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeEquipment(instanceId);
                            }}
                            className="p-0.5 rounded hover:bg-red-600"
                            title="Remove"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Empty Rack Hover Areas */}
                {!draggedEquipment && Array.from({ length: selectedRackSize }, (_, i) => {
                  const isOccupied = rackEquipment.some(eq => {
                    const startUnit = eq.startUnit;
                    const endUnit = startUnit + (eq.equipment.units || 1);
                    return i >= startUnit && i < endUnit;
                  });

                  if (isOccupied) return null;

                  return (
                    <div
                      key={`empty-${i}`}
                      onClick={() => {
                        if (openUnits > 0) {
                          setTargetUnit(i);
                          setShowEquipmentList(true);
                        }
                      }}
                      className="absolute left-8 right-2 rounded transition-all group"
                      style={{
                        top: i * 30 + 3,
                        height: 30 - 4,
                        zIndex: 0,
                        cursor: openUnits > 0 ? 'pointer' : 'not-allowed',
                        opacity: openUnits > 0 ? 1 : 0.4,
                      }}
                      onMouseEnter={e => {
                        if (openUnits > 0) {
                          e.currentTarget.style.backgroundColor = emptyHoverBg;
                          e.currentTarget.style.border = `1px solid ${emptyHoverBorder}`;
                        }
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = '';
                        e.currentTarget.style.border = '1px solid transparent';
                      }}
                    >
                      <div
                        className="hidden group-hover:flex h-full items-center justify-center text-xs font-medium"
                        style={{ color: isDark ? '#86efac' : '#16a34a' }}
                      >
                        {openUnits > 0 && (
                          <>
                            <Plus size={12} className="mr-1" />
                            Add
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                </div>
              </div>
            </div>

            {/* Right — equipment picker */}
            <div
              className="w-72 flex-shrink-0 flex flex-col overflow-hidden"
              style={{ borderLeft: `1px solid ${theme.border}`, backgroundColor: theme.cardBg }}
            >
              <div
                className="flex items-center justify-between px-4 py-3 flex-shrink-0"
                style={{ borderBottom: `1px solid ${theme.border}` }}
              >
                <div>
                  <p className="text-[11px] uppercase tracking-wide font-medium" style={{ color: theme.muted }}>
                    Equipment
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: theme.text }}>
                    {showEquipmentList
                      ? targetUnit !== null
                        ? `Add to unit ${targetUnit + 1}`
                        : 'Add equipment'
                      : `${rackEquipment.length} installed`}
                  </p>
                </div>
                {showEquipmentList ? (
                  <button
                    type="button"
                    onClick={() => {
                      setShowEquipmentList(false);
                      setTargetUnit(null);
                      setEquipmentSearch('');
                    }}
                    className="p-1 rounded transition-colors"
                    style={{ color: theme.muted }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.rowHover)}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                    title="Close picker"
                  >
                    <X size={14} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      if (openUnits > 0) {
                        setTargetUnit(null);
                        setShowEquipmentList(true);
                      }
                    }}
                    disabled={openUnits <= 0}
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: openUnits > 0 ? '#2563eb' : theme.inputBg,
                      color: openUnits > 0 ? '#ffffff' : theme.muted,
                    }}
                  >
                    <Plus size={12} />
                    Add
                  </button>
                )}
              </div>

              {showEquipmentList && (
                <div
                  className="px-3 py-2 flex-shrink-0"
                  style={{ borderBottom: `1px solid ${theme.border}` }}
                >
                  <div className="relative">
                    <Search
                      size={13}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                      style={{ color: theme.muted }}
                    />
                    <input
                      type="text"
                      value={equipmentSearch}
                      onChange={(e) => setEquipmentSearch(e.target.value)}
                      placeholder="Search equipment…"
                      className="w-full h-7 pl-8 pr-2 text-xs rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                      style={{
                        backgroundColor: theme.inputBg,
                        borderColor: theme.border,
                        color: theme.text,
                      }}
                      autoFocus
                    />
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-y-auto min-h-0">
                {showEquipmentList ? (
                  filteredEquipment.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <p className="text-xs" style={{ color: theme.muted }}>
                        {searchQuery
                          ? 'No equipment matches your search.'
                          : `No equipment fits in the remaining space${targetUnit !== null ? ` at unit ${targetUnit + 1}` : ''}.`}
                      </p>
                    </div>
                  ) : (
                    <div>
                      {filteredEquipment.map(equipment => (
                        <button
                          key={equipment.id}
                          type="button"
                          onClick={() => addEquipmentToRack(equipment)}
                          className="w-full text-left px-4 py-2.5 transition-colors border-b"
                          style={{ borderColor: theme.border }}
                          onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.rowHover)}
                          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                        >
                          <div className="text-xs font-medium truncate" style={{ color: theme.text }}>
                            {equipment.name}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span
                              className="inline-flex h-4 items-center px-1.5 rounded text-[10px] font-medium"
                              style={{
                                backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe',
                                color: isDark ? '#93c5fd' : '#1d4ed8',
                              }}
                            >
                              {equipment.units}U
                            </span>
                            <span className="text-[11px] truncate" style={{ color: theme.muted }}>
                              {equipment.manufacturer}
                            </span>
                            {equipment.price != null && (
                              <span className="text-[11px] ml-auto flex-shrink-0" style={{ color: isDark ? '#86efac' : '#16a34a' }}>
                                ${equipment.price}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )
                ) : (
                  <div>
                    {rackEquipment.map((rackEq, index) => (
                      <div
                        key={rackEq.instanceId}
                        className="flex items-center gap-2 px-4 py-2.5 border-b"
                        style={{ borderColor: theme.border }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.rowHover)}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                      >
                        <button
                          type="button"
                          onClick={() => handleEquipmentClick(rackEq, index)}
                          className="flex-1 min-w-0 text-left"
                        >
                          <div className="text-xs font-medium truncate" style={{ color: theme.text }}>
                            {rackEq.equipment.name}
                          </div>
                          <div className="text-[11px] mt-0.5" style={{ color: theme.muted }}>
                            U{rackEq.startUnit + 1}
                            {(rackEq.equipment.units || 1) > 1
                              ? `–${rackEq.startUnit + (rackEq.equipment.units || 1)}`
                              : ''}
                            {' · '}
                            {rackEq.equipment.units || 1}U
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEquipmentClick(rackEq, index)}
                          className="p-1 rounded transition-colors flex-shrink-0"
                          style={{ color: theme.muted }}
                          title="Configure"
                        >
                          <Settings size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeEquipment(rackEq.instanceId!)}
                          className="p-1 rounded transition-colors flex-shrink-0"
                          style={{ color: theme.muted }}
                          title="Remove"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-between px-5 py-3 flex-shrink-0"
            style={{ borderTop: `1px solid ${theme.border}`, backgroundColor: theme.barBg }}
          >
            <p className="text-xs" style={{ color: theme.muted }}>
              {editingRack
                ? 'Changes apply to this rack on the design.'
                : 'Finish to place this rack on the canvas.'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="h-7 px-2.5 rounded text-xs font-medium transition-colors border"
                style={{
                  borderColor: theme.border,
                  color: theme.text,
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.rowHover)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFinish}
                className="inline-flex items-center h-7 px-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors text-xs font-medium"
              >
                {editingRack ? 'Save changes' : 'Place on canvas'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Unlink site confirmation */}
      {confirmUnlinkOpen && (
        <>
          <div
            className="fixed inset-0 z-[60] transition-opacity duration-150"
            style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
            onClick={() => setConfirmUnlinkOpen(false)}
          />
          <div className="fixed inset-0 z-[61] flex items-center justify-center pointer-events-none p-4">
            <div
              className="pointer-events-auto w-full max-w-sm rounded-lg border shadow-2xl overflow-hidden"
              style={{
                backgroundColor: theme.cardBg,
                borderColor: theme.border,
              }}
              onClick={e => e.stopPropagation()}
            >
              <div className="px-5 pt-5 pb-3">
                <div className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2',
                      color: isDark ? '#fca5a5' : '#b91c1c',
                    }}
                  >
                    <Unlink size={16} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold" style={{ color: theme.text }}>
                      Unlink site?
                    </h3>
                    <p className="text-xs mt-1.5 leading-relaxed" style={{ color: theme.muted }}>
                      {linkedSite
                        ? <>This rack will no longer appear on <span style={{ color: theme.text }}>{linkedSite.name}</span>'s equipment list.</>
                        : 'This rack will no longer be linked to a site.'}
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="flex items-center justify-end gap-2 px-5 py-3"
                style={{ borderTop: `1px solid ${theme.border}`, backgroundColor: theme.barBg }}
              >
                <button
                  type="button"
                  onClick={() => setConfirmUnlinkOpen(false)}
                  className="h-7 px-2.5 rounded text-xs font-medium transition-colors border"
                  style={{
                    borderColor: theme.border,
                    color: theme.text,
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.rowHover)}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmUnlinkSite}
                  className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded text-xs font-medium text-white transition-colors bg-red-600 hover:bg-red-700"
                >
                  <Unlink size={12} />
                  Unlink site
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default RackBuilder;
